import React, { useState } from 'react'
import RemovableChip from './RemovableChip'

interface ChipListInputProps {
  values: string[]
  onChange: (values: string[]) => void
  placeholder: string
  // Returns a message when the entry should be refused, or null to accept it.
  validate?: (value: string) => string | null
  inputId?: string
  disabled?: boolean
}

// A list of short free-text values entered one at a time - phone numbers, email addresses. Enter or
// a comma commits the draft, blur commits it too so a value typed and abandoned is not silently
// lost, and Backspace on an empty field takes the last chip back.
const ChipListInput = ({
  values,
  onChange,
  placeholder,
  validate,
  inputId,
  disabled = false
}: ChipListInputProps) => {
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)

  const commitDraft = () => {
    const entry = draft.trim().replace(/,+$/, '').trim()
    if (entry === '') {
      setError(null)
      return
    }

    const message = validate ? validate(entry) : null
    if (message) {
      setError(message)
      return
    }

    if (!values.includes(entry)) onChange([...values, entry])
    setDraft('')
    setError(null)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      commitDraft()
      return
    }

    if (event.key === 'Backspace' && draft === '' && values.length > 0) {
      onChange(values.slice(0, -1))
    }
  }

  const remove = (value: string) => onChange(values.filter((entry) => entry !== value))

  return (
    <>
      <div className={`chip-list-input ${disabled ? 'chip-list-input--disabled' : ''}`}>
        {values.map((value) => (
          <RemovableChip
            key={value}
            label={value}
            variant="outline"
            disabled={disabled}
            onRemove={() => remove(value)}
          />
        ))}

        <input
          id={inputId}
          type="text"
          className="chip-list-input__field"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commitDraft}
        />
      </div>

      {error && <div className="text-danger small mt-1">{error}</div>}
    </>
  )
}

export default ChipListInput
